import { useEffect, useRef, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { PaperPlaneTilt, Warning, EnvelopeSimple } from "@phosphor-icons/react";
import { fmtDateTime } from "../../lib/dateUtil";

/** Email a filed HSE pack to the client's contacts. Honours the client's email switch — when it's off the send is held until the
 *  staff member explicitly ticks "send anyway". */
export default function SendPackPanel({ packId, onSent }) {
  const [d, setD] = useState(null);
  const [picked, setPicked] = useState({});
  const [extra, setExtra] = useState("");
  const [message, setMessage] = useState("");
  const [anyway, setAnyway] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!packId) return;
    setD(null); setPicked({}); setExtra(""); setAnyway(false);
    API.get(`/hse/pack/${packId}/recipients`).then(({ data }) => { setD(data); setPicked(Object.fromEntries(data.recipients.slice(0, 1).map(r => [r.email, true]))); }).catch(e => toast.error(formatApiError(e)));
  }, [packId]);
  useEffect(() => { if (d) ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [d]);

  if (!d) return <p className="text-xs text-muted-foreground" data-testid="send-pack-loading">Loading contacts…</p>;
  const to = [...Object.keys(picked).filter(k => picked[k]), ...extra.split(/[,;\s]+/).map(x => x.trim().toLowerCase()).filter(x => x.includes("@"))];
  const off = !d.emails_enabled;
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await API.post(`/hse/pack/${packId}/send`, { to, message, send_anyway: anyway });
      toast.success(`${d.pack.reference} sent to ${data.sent.join(", ")}${data.failed.length ? ` · failed: ${data.failed.join(", ")}` : ""}`);
      setD(prev => ({ ...prev, pack: { ...prev.pack, sent: [...(prev.pack.sent || []), { at: new Date().toISOString(), to: data.sent }] } }));
      setExtra(""); setMessage("");
      onSent?.(data);
    } catch (e) {
      if (e?.response?.status === 409 && e.response.data?.held) toast.error(e.response.data.detail); else toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };
  const last = (d.pack.sent || []).slice(-1)[0];
  return (
    <div ref={ref} className="border border-border rounded-lg p-3 space-y-3 bg-muted/30" data-testid="send-pack-panel">
      <div className="flex items-center gap-2">
        <EnvelopeSimple size={16} className="text-primary" />
        <p className="text-sm font-semibold flex-1">Send {d.pack.reference} to {d.name}</p>
        {last && <span className="text-[11px] text-muted-foreground" data-testid="send-pack-last">Last sent {fmtDateTime(last.at)} to {last.to.length} recipient{last.to.length === 1 ? "" : "s"}</span>}
      </div>
      {off && (
        <div className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-300 rounded-md p-2 dark:bg-amber-950/20" data-testid="send-pack-emails-off">
          <Warning size={14} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium">{d.name} has emails switched off.</p>
            <p className="text-muted-foreground">Automatic emails to them are held. You can still send this one deliberately.</p>
            <label className="flex items-center gap-2 mt-1.5 font-medium"><input type="checkbox" checked={anyway} onChange={e => setAnyway(e.target.checked)} data-testid="send-pack-anyway" /> Send anyway — I've checked they want it</label>
          </div>
        </div>
      )}
      <div className="space-y-1.5">
        <Label className="text-xs">Recipients</Label>
        {d.recipients.length === 0 && <p className="text-xs text-muted-foreground" data-testid="send-pack-no-contacts">No contact emails on this client yet — add one below.</p>}
        <div className="grid sm:grid-cols-2 gap-1">
          {d.recipients.map(r => (
            <label key={r.email} className="flex items-center gap-2 text-xs border border-border rounded-md px-2 py-1.5 bg-background cursor-pointer" data-testid={`send-pack-recipient-${r.email}`}>
              <input type="checkbox" checked={!!picked[r.email]} onChange={e => setPicked(p => ({ ...p, [r.email]: e.target.checked }))} />
              <span className="flex-1 min-w-0"><span className="font-medium">{r.name}</span>{r.role ? <span className="text-muted-foreground"> · {r.role}</span> : null}<br /><span className="text-muted-foreground">{r.email}</span></span>
            </label>
          ))}
        </div>
        <Input value={extra} onChange={e => setExtra(e.target.value)} placeholder="Add another address (comma-separated)" className="h-8 text-xs" data-testid="send-pack-extra" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Message (optional)</Label>
        <Textarea value={message} onChange={e => setMessage(e.target.value)} rows={2} placeholder="e.g. As requested for the tender submission — happy to talk through any of it." className="text-sm" data-testid="send-pack-message" />
      </div>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-muted-foreground">{to.length} recipient{to.length === 1 ? "" : "s"} · the PDF is attached and the email is branded for your company</p>
        <Button size="sm" onClick={send} disabled={busy || !to.length || (off && !anyway)} data-testid="send-pack-btn"><PaperPlaneTilt size={14} className="mr-1" /> {busy ? "Sending…" : "Send pack"}</Button>
      </div>
    </div>
  );
}
